import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Moon, Sun, SlidersHorizontal, User, X } from 'lucide-react';
import { useFilters } from '../context/FiltersContext';
import { useTheme } from '../context/ThemeContext';
import { triggerHaptic } from '../utils/haptics';
import { FiltersSheet } from './Filters/FiltersSheet';
import { useResponsiveBreakpoints } from '../hooks/useResponsiveBreakpoints';
import { useScrollDirection } from '../hooks/useScrollDirection';
import { isEnginePath } from '../constants/engineRoutes';

const Header: React.FC = () => {
  const location = useLocation();
  const { filters, setFilters } = useFilters();
  const { theme, toggle: toggleTheme } = useTheme();
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [filtersOpen, setFiltersOpen] = React.useState(false);
  const { isXs } = useResponsiveBreakpoints();
  const { visible } = useScrollDirection();

  const isLandingPage = location.pathname === '/';
  const isDataRoute = isEnginePath(location.pathname);

  // Active filters count (for desktop badge)
  const activeCount = [
    filters.kommunId && filters.kommunId !== 'null',
    filters.huvudmanId && filters.huvudmanId !== '',
    filters.skolaId && filters.skolaId !== 'null',
    filters.kpiId,
    filters.compareScope !== 'municipality'
  ].filter(Boolean).length;

  // Don't render Header at all on landing page (defense in depth)
  if (isLandingPage) {
    return null;
  }

  return (
    <header className={`fixed top-0 left-0 right-0 z-[2100] glass transition-transform duration-300 ${visible ? 'translate-y-0' : '-translate-y-full'}`}>
      <div className="mx-auto max-w-[1200px] px-4 py-2">
        <div className="flex items-center gap-2">
          {/* Logo and Brand - compressed for mobile */}
          <div className="flex items-center gap-2 min-w-0">
            <Link to="/" className="flex items-center gap-1.5 sm:gap-2 group shrink-0">
              <img
                src="/logo.svg"
                alt="Kolada Logo"
                className="h-6 w-6 sm:h-8 sm:w-8 transition-transform duration-200 group-hover:scale-105"
              />
              <div className="ui-label ell-1 font-semibold text-sm sm:text-base text-gray-900 dark:text-white group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-colors duration-200">
                eduInsights
              </div>
            </Link>
            {/* Tagline hidden on small screens and ultra-small screens */}
            <div className={`${isXs ? 'hidden' : 'hidden sm:block'} text-mobile-sm text-slate-500 ell-1`}>
              Skolanalys & Jämförelser
            </div>
          </div>

          {/* Navigation - Desktop */}
          <nav className="hidden sm:flex items-center space-x-6 ml-6">
            <Link
              to="/kommun"
              className={`font-medium transition-colors duration-200 relative group ${location.pathname === '/kommun'
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400'
                }`}
            >
              Kommunöversikt
              <span className={`absolute -bottom-1 left-0 h-0.5 bg-emerald-500 transition-all duration-200 ${location.pathname === '/kommun'
                ? 'w-full'
                : 'w-0 group-hover:w-full'
                }`} />
            </Link>
            <Link
              to="/about"
              className={`font-medium transition-colors duration-200 relative group ${location.pathname === '/about'
                ? 'text-emerald-600 dark:text-emerald-400'
                : 'text-gray-600 dark:text-gray-300 hover:text-emerald-600 dark:hover:text-emerald-400'
                }`}
            >
              Om
              <span className={`absolute -bottom-1 left-0 h-0.5 bg-emerald-500 transition-all duration-200 ${location.pathname === '/about'
                ? 'w-full'
                : 'w-0 group-hover:w-full'
                }`} />
            </Link>
          </nav>

          {/* Actions */}
          <div className="ml-auto flex items-center gap-2">
            {/* Theme Toggle - Desktop only */}
            <button
              onClick={() => {
                triggerHaptic('light');
                toggleTheme();
              }}
              className="hidden sm:flex items-center justify-center p-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors"
              aria-label={theme === 'dark' ? 'Byt till ljust läge' : 'Byt till mörkt läge'}
            >
              {theme === 'dark' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </button>

            {/* Filter Toggle - Desktop only (hide on landing page) */}
            {isDataRoute && (
              <button
                id="filter-toggle"
                onClick={() => {
                  triggerHaptic('medium');
                  setFiltersOpen(true);
                }}
                className={`
                  hidden sm:flex h-10 items-center gap-2 px-4 rounded-xl font-medium transition-all
                  ${activeCount > 0
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 ring-1 ring-emerald-200 dark:ring-emerald-800'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700'
                  }
                `}
                aria-label="Öppna filter"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span>Filter</span>
                {activeCount > 0 && (
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-emerald-600 text-[10px] font-bold text-white">
                    {activeCount}
                  </span>
                )}
              </button>
            )}

            {/* Mobile Menu Toggle - Shows user silhouette for About section */}
            <button
              onClick={() => {
                triggerHaptic('light');
                setMenuOpen(!menuOpen);
              }}
              className="sm:hidden p-2 rounded-xl text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800 transition-colors"
              aria-label={menuOpen ? 'Stäng meny' : 'Öppna meny'}
            >
              {menuOpen ? <X className="w-6 h-6" /> : <User className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile nav panel (for non-primary links) */}
        {menuOpen && (
          <div className="sm:hidden mt-3 flex flex-col gap-2 p-1">
            {/* Primary links are in bottom nav, but 'About' is here */}
            <Link to="/about" onClick={() => setMenuOpen(false)} className="px-3 py-3 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-900 dark:text-slate-100 font-medium">Om</Link>
          </div>
        )}
      </div>
      {/* Filters Sheet - Desktop version also triggered here (only when NOT on landing page) */}
      {isDataRoute && (
        <FiltersSheet
          isOpen={filtersOpen}
          onClose={() => setFiltersOpen(false)}
          value={filters}
          onChange={setFilters}
        />
      )}
    </header>
  );
};

export default Header;
